<template>
    <div class="app-progress-page">
      <!-- Background Elements -->
      <div class="page-grid"></div>
      <div class="page-orb orb-1"></div>
      <div class="page-orb orb-2"></div>
  
      <!-- Top Navigation -->
      <div class="top-nav">
        <div class="nav-container">
          <div class="nav-brand">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <path d="M28 10.6667L16 22.6667L4 10.6667M16 4L4 10.6667V21.3333C4 23.9 9.33333 28 16 28C22.6667 28 28 23.9 28 21.3333V10.6667L16 4Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>MANAGE APP PROGRESS</span>
          </div>
          <button class="nav-btn" @click="goBack">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15.8333 10H4.16667M4.16667 10L10 15.8333M4.16667 10L10 4.16667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Back to Settings
          </button>
        </div>
      </div>
  
      <!-- Main Content -->
      <div class="progress-container">
        <!-- Hero Section -->
        <div class="progress-hero">
          <div class="hero-text">
            <h1 class="hero-title">Update Community Journeys</h1>
            <p class="hero-subtitle">Pick a community, review their journey, and set the next stage</p>
          </div>
          <div class="stats-card">
            <div class="stat-item">
              <div class="stat-label">Communities</div>
              <div class="stat-value">{{ companies.length }}</div>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
              <div class="stat-label">Completed</div>
              <div class="stat-value">{{ completedCount }}</div>
            </div>
          </div>
        </div>
  
        <!-- Main Card -->
        <div class="main-card">
          <h2 class="card-title">Select Community and Update Progress</h2>
  
          <!-- Community Search/Select -->
          <div class="customer-select-section">
            <div class="search-wrapper">
              <svg class="search-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M17.5 17.5L13.875 13.875" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search by community name or owner email..."
                @focus="showDropdown = true"
                @input="showDropdown = true"
              />
              <button v-if="searchQuery" class="clear-btn" @click="clearSearch">✕</button>
            </div>
  
            <!-- Dropdown Results -->
            <div v-if="showDropdown && filteredCompanies.length > 0" class="customer-dropdown">
              <div
                v-for="company in filteredCompanies"
                :key="company.id"
                class="customer-option"
                @click="selectCompany(company)"
              >
                <div class="customer-avatar">{{ company.initials }}</div>
                <div class="customer-info">
                  <div class="customer-name">{{ company.businessName || '(No name)' }}</div>
                  <div class="customer-email">{{ company.ownerEmail }}</div>
                </div>
                <div :class="['progress-badge', `badge-${getStageClass(company.appProgress)}`]">
                  {{ company.appProgress }}
                </div>
              </div>
            </div>
  
            <!-- No Results -->
            <div v-else-if="showDropdown && searchQuery && filteredCompanies.length === 0" class="no-results">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <path d="M24 44C35.0457 44 44 35.0457 44 24C44 12.9543 35.0457 4 24 4C12.9543 4 4 12.9543 4 24C4 35.0457 12.9543 44 24 44Z" stroke="currentColor" stroke-width="3"/>
                <path d="M16 16L32 32M32 16L16 32" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
              </svg>
              <p>No communities found</p>
            </div>
          </div>
  
          <!-- Info Alert (No Community Selected) -->
          <div v-if="!selected" class="info-alert">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 18.3333C14.6024 18.3333 18.3334 14.6024 18.3334 10C18.3334 5.39763 14.6024 1.66667 10 1.66667C5.39765 1.66667 1.66669 5.39763 1.66669 10C1.66669 14.6024 5.39765 18.3333 10 18.3333Z" stroke="currentColor" stroke-width="1.5"/>
              <path d="M10 6.66667V10M10 13.3333H10.0083" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
            <span>Pick a community above to see their journey</span>
          </div>
  
          <!-- Selected Community Summary -->
          <transition name="slide-down">
            <div v-if="selected" class="customer-summary">
              <div class="summary-left">
                <div class="summary-avatar">{{ selected.initials }}</div>
                <div class="summary-info">
                  <div class="summary-label">Community</div>
                  <div class="summary-name">{{ selected.businessName || selected.ownerEmail }}</div>
                </div>
              </div>
              <div class="summary-badges">
                <div :class="['current-badge', `badge-${getStageClass(selected.appProgress)}`]">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M14 8L8 14L2 8M8 2V14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  {{ selected.appProgress }}
                </div>
                <div v-if="nextStep" class="next-badge">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M8 14L14 8M14 8L8 2M14 8H2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  Next: {{ nextStep }}
                </div>
              </div>
              <div class="summary-date">
                Last updated: {{ lastStageDate || '—' }}
              </div>
            </div>
          </transition>
  
          <!-- Timeline Journey -->
          <transition name="fade">
            <div v-if="selected" class="timeline-section">
              <h3 class="timeline-title">App Progress Journey</h3>
              <div class="timeline">
                <div
                  v-for="(step, index) in progressOptions"
                  :key="step"
                  :class="['timeline-item', { completed: isStepComplete(step) }]"
                >
                  <div class="timeline-marker">
                    <svg v-if="isStepComplete(step)" width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M16.6667 5L7.50004 14.1667L3.33337 10" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="2"/>
                    </svg>
                  </div>
                  <div v-if="index < progressOptions.length - 1" class="timeline-line"></div>
                  <div class="timeline-content">
                    <div class="timeline-step-name">{{ step }}</div>
                    <div class="timeline-date">{{ stageDate(step) || (isStepComplete(step) ? '—' : '') }}</div>
                  </div>
                </div>
              </div>
            </div>
          </transition>
  
          <!-- Stage Selection -->
          <transition name="fade">
            <div v-if="selected" class="stage-section">
              <div class="quick-select">
                <span class="quick-label">Quick set:</span>
                <div class="stage-chips">
                  <button
                    v-for="stage in progressOptions"
                    :key="stage"
                    :class="['stage-chip', `chip-${getStageClass(stage)}`, { active: newProgress === stage }]"
                    @click="newProgress = stage"
                  >
                    {{ stage }}
                  </button>
                </div>
              </div>
  
              <div class="stage-select-wrapper">
                <label>Or choose a new stage</label>
                <select v-model="newProgress" class="stage-select">
                  <option value="">Select stage...</option>
                  <option v-for="stage in progressOptions" :key="stage" :value="stage">
                    {{ stage }}
                  </option>
                </select>
              </div>

              <div class="note-wrapper">
                <label for="progress-note">Note to the community (optional)</label>
                <textarea
                  id="progress-note"
                  v-model="progressNote"
                  class="note-input"
                  rows="3"
                  maxlength="1200"
                  placeholder="e.g. Lovely meeting your team today. We'll send the menu layout on Friday."
                ></textarea>
                <div class="note-hint">Shown in the stage email, signed with your name.</div>
              </div>

              <p class="email-note">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M2 4.5L8 9L14 4.5M2.5 3H13.5C14.0523 3 14.5 3.44772 14.5 4V12C14.5 12.5523 14.0523 13 13.5 13H2.5C1.94772 13 1.5 12.5523 1.5 12V4C1.5 3.44772 1.94772 3 2.5 3Z" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                The community's owner gets an email automatically when this stage changes.
              </p>
            </div>
          </transition>
  
          <!-- Update Button -->
          <button
            class="btn-update"
            :disabled="!selected || !newProgress || newProgress === selected?.appProgress || updating"
            @click="updateProgress"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C12.5 2.5 14.75 3.75 16.25 5.625M16.25 5.625V2.5M16.25 5.625H13.125" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            {{ updating ? 'Updating...' : 'Update App Progress' }}
          </button>
  
          <!-- Message Alert -->
          <transition name="fade">
            <div v-if="message" :class="['message-alert', messageType]">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path v-if="messageType === 'success'" d="M16.6667 5L7.50004 14.1667L3.33337 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path v-else d="M10 18.3333C14.6024 18.3333 18.3334 14.6024 18.3334 10C18.3334 5.39763 14.6024 1.66667 10 1.66667C5.39765 1.66667 1.66669 5.39763 1.66669 10C1.66669 14.6024 5.39765 18.3333 10 18.3333Z" stroke="currentColor" stroke-width="1.5"/>
              </svg>
              <span>{{ message }}</span>
              <button @click="message = ''">✕</button>
            </div>
          </transition>
        </div>
      </div>
  
      <!-- Snackbar -->
      <div v-if="snack.show" class="snackbar">
        <span>{{ snack.msg }}</span>
        <button @click="snack.show = false">✕</button>
      </div>
    </div>
  </template>
  
<script>
  import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
  import { db, auth } from '../firebase'
  import {
    collection, getDocs, updateDoc, doc, serverTimestamp
  } from 'firebase/firestore'
  import router from '../router'
  
  export default {
    name: 'ManageAppProgressView',
    setup() {
      const companies = ref([])
      const loadingCompanies = ref(true)
      const selectedId = ref(null)
      const searchQuery = ref('')
      const showDropdown = ref(false)
      const newProgress = ref('')
      const progressNote = ref('')
      const updating = ref(false)
      const message = ref('')
      const messageType = ref('success')
      const snack = ref({ show: false, msg: '' })
  
      // Matches the journey shown on the customer's own Dashboard
      // (CustomerDashboardView.vue) and staff's per-company detail page
      // (CustomerDetailView.vue) -- Heartline Hub is a shared platform
      // being configured per community, not a custom-built app, so these
      // track account setup rather than development milestones.
      const progressOptions = ['Not Started', 'Designing', 'Developing', 'Testing', 'Completed']
  
      const selected = computed(() => companies.value.find(c => c.id === selectedId.value) || null)
  
      const filteredCompanies = computed(() => {
        const q = searchQuery.value.toLowerCase().trim()
        if (!q) return companies.value
        return companies.value.filter(c =>
          (c.businessName || '').toLowerCase().includes(q) ||
          (c.ownerEmail || '').toLowerCase().includes(q)
        )
      })
  
      const progressIndex = computed(() =>
        selected.value ? progressOptions.indexOf(selected.value.appProgress) : -1
      )
  
      const nextStep = computed(() => {
        if (progressIndex.value < 0) return ''
        const nxt = progressOptions[progressIndex.value + 1]
        return nxt || ''
      })
  
      const completedCount = computed(() =>
        companies.value.filter(c => c.appProgress === 'Completed').length
      )
  
      const lastStageDate = computed(() => {
        if (!selected.value) return ''
        const key = selected.value.appProgress
        return stageDate(key)
      })
  
      function initialsFrom(name = '', email = '') {
        const nm = (name || '').trim()
        if (nm) return nm.split(/\s+/).map(p => p[0]).join('').slice(0, 2).toUpperCase()
        return (email.split('@')[0] || '??').slice(0, 2).toUpperCase()
      }
  
      function getStageClass(stage) {
        switch ((stage || '').toLowerCase()) {
          case 'not started': return 'not-started'
          case 'designing': return 'designing'
          case 'developing': return 'developing'
          case 'testing': return 'testing'
          case 'completed': return 'completed'
          default: return 'default'
        }
      }
  
      function prettyDate(ts) {
        const d = ts?.toDate ? ts.toDate() : (ts?.seconds ? new Date(ts.seconds * 1000) : (ts instanceof Date ? ts : null))
        return d ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : ''
      }
  
      function stageDate(step) {
        if (!selected.value?.appProgressDates) return ''
        return prettyDate(selected.value.appProgressDates[step])
      }
  
      function isStepComplete(step) {
        if (!selected.value) return false
        return progressOptions.indexOf(step) <= progressOptions.indexOf(selected.value.appProgress || 'Not Started')
      }
  
      async function fetchCompanies() {
        loadingCompanies.value = true
        try {
          // Reads customers/{companyId} directly -- NOT users filtered by
          // role -- since appProgress lives on the shared company record,
          // not any individual person's own account. A community can have
          // many staff/family logins; only one of them is even the
          // "owner", and appProgress isn't tied to that owner's uid at all.
          const snap = await getDocs(collection(db, 'customers'))
          companies.value = snap.docs.map(d => {
            const data = d.data() || {}
            return {
              id: d.id,
              businessName: data.businessName || '',
              ownerEmail: data.ownerEmail || '',
              initials: initialsFrom(data.businessName, data.ownerEmail),
              appProgress: data.appProgress || 'Not Started',
              appProgressDates: data.appProgressDates || {}
            }
          })
        } catch (e) {
          console.error('fetchCompanies', e)
          message.value = '\u274c Failed to load communities.'
          messageType.value = 'error'
        } finally {
          loadingCompanies.value = false
        }
      }
  
      function selectCompany(company) {
        selectedId.value = company.id
        searchQuery.value = company.businessName || company.ownerEmail
        showDropdown.value = false
        newProgress.value = ''
        message.value = ''
      }
  
      function clearSearch() {
        searchQuery.value = ''
        selectedId.value = null
        showDropdown.value = false
        newProgress.value = ''
      }
  
      async function updateProgress() {
        if (!selected.value || !newProgress.value) return
        updating.value = true
        try {
          // Single write to the community's own record. The previous
          // version of this page wrote to users/{uid} and attempted a
          // best-effort fallback write to customers/{uid} using the
          // selected person's own uid as if it were the company id --
          // that's not how the data model works (a community can have
          // many staff/family logins, so appProgress can't live on any one
          // of them, and uid never actually equals the real companyId).
          // In practice that meant the real customers/{companyId} record
          // almost never got updated, and notifyProgressChange (the
          // Firestore trigger in index.ts that emails the owner) almost
          // never fired. This writes directly to the correct document, so
          // that trigger fires reliably every time.
          // progressNote/progressNoteBy are read by notifyProgressChange for
          // the stage email, then cleared by it.
          const note = progressNote.value.trim()
          const me = auth.currentUser
          await updateDoc(doc(db, 'customers', selected.value.id), {
            appProgress: newProgress.value,
            [`appProgressDates.${newProgress.value}`]: serverTimestamp(),
            ...(note
              ? { progressNote: note, progressNoteBy: (me && (me.displayName || (me.email || '').split('@')[0])) || 'The team' }
              : {})
          })
          progressNote.value = ''
  
          snack.value = { show: true, msg: '\u2713 Progress updated!' }
          message.value = '\u2705 App progress updated \u2014 the community owner has been emailed.'
          messageType.value = 'success'
  
          await fetchCompanies()
          selectedId.value = selected.value.id
          searchQuery.value = selected.value.businessName || selected.value.ownerEmail
          newProgress.value = ''
        } catch (e) {
          console.error('updateProgress', e)
          message.value = '\u274c Failed to update app progress.'
          messageType.value = 'error'
        } finally {
          updating.value = false
        }
      }
  
      function goBack() {
        router.push('/admin-settings')
      }
  
      function handleClickOutside(e) {
        const searchWrapper = document.querySelector('.search-wrapper')
        const dropdown = document.querySelector('.customer-dropdown')
        if (searchWrapper && !searchWrapper.contains(e.target) && dropdown && !dropdown.contains(e.target)) {
          showDropdown.value = false
        }
      }
  
      onMounted(() => {
        fetchCompanies()
        document.addEventListener('click', handleClickOutside)
      })
  
      onBeforeUnmount(() => {
        document.removeEventListener('click', handleClickOutside)
      })
  
      return {
        companies,
        loadingCompanies,
        selectedId,
        searchQuery,
        showDropdown,
        newProgress,
        progressNote,
        updating,
        message,
        messageType,
        snack,
        progressOptions,
        selected,
        filteredCompanies,
        progressIndex,
        nextStep,
        completedCount,
        lastStageDate,
        getStageClass,
        stageDate,
        isStepComplete,
        selectCompany,
        clearSearch,
        updateProgress,
        goBack
      }
    }
  }
  </script>
  
  <style scoped>
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600;9..144,700&family=Inter:wght@400;500;600;700&display=swap');

    .app-progress-page {
      min-height: 100vh;
      background: #FBF7EF;
      position: relative;
      font-family: 'Inter', sans-serif;
      padding-bottom: 80px;
      overflow-x: hidden;
    }

    .app-progress-page .hero-title,
    .app-progress-page .card-title,
    .app-progress-page .timeline-title {
      font-family: 'Fraunces', serif;
      font-weight: 500;
      letter-spacing: -0.005em;
    }
    
    /* ==================== BACKGROUND ELEMENTS ==================== */
    .page-grid {
      position: fixed;
      inset: 0;
      background-image:
        linear-gradient(to right, rgba(30, 55, 51, 0.06) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(30, 55, 51, 0.06) 1px, transparent 1px);
      background-size: 60px 60px;
      mask-image: radial-gradient(ellipse 70% 60% at 50% 50%, black 20%, transparent 70%);
      animation: grid-flow 35s linear infinite;
      pointer-events: none;
      z-index: 0;
    }
    
    @keyframes grid-flow {
      0% { transform: translate(0, 0); }
      100% { transform: translate(60px, 60px); }
    }
    
    .page-orb {
      position: fixed;
      border-radius: 50%;
      filter: blur(140px);
      opacity: 0.3;
      animation: orb-float 18s ease-in-out infinite;
      pointer-events: none;
      z-index: 0;
    }
    
    .orb-1 {
      width: 600px;
      height: 600px;
      background: radial-gradient(circle, rgba(228,201,137,0.5), transparent);
      top: -200px;
      right: -200px;
    }
    
    .orb-2 {
      width: 500px;
      height: 500px;
      background: radial-gradient(circle, rgba(30,55,51,0.3), transparent);
      bottom: -150px;
      left: -150px;
      animation-delay: 6s;
    }
    
    @keyframes orb-float {
      0%, 100% { transform: translate(0, 0) scale(1); }
      33% { transform: translate(50px, -50px) scale(1.1); }
      66% { transform: translate(-40px, 40px) scale(0.9); }
    }
    
    /* ==================== TOP NAVIGATION ==================== */
    .top-nav {
      position: sticky;
      top: 0;
      z-index: 100;
      background: linear-gradient(160deg, #1E3733 0%, #2F4F4A 100%);
      border-bottom: 1px solid rgba(251, 247, 239, 0.12);
      box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
    }
    
    .nav-container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 16px 24px;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    
    .nav-brand {
      display: flex;
      align-items: center;
      gap: 12px;
      color: #ffffff;
      font-size: 14px;
      font-weight: 800;
      letter-spacing: 0.05em;
    }
    
    .nav-brand svg {
      filter: drop-shadow(0 0 8px rgba(255,255,255,0.5));
    }
    
    .nav-btn {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 18px;
      background: rgba(255, 255, 255, 0.15);
      border: 2px solid rgba(255, 255, 255, 0.2);
      border-radius: 10px;
      color: #ffffff;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;
      font-family: 'Inter', sans-serif;
    }
    
    .nav-btn:hover {
      background: rgba(255, 255, 255, 0.25);
      border-color: rgba(255, 255, 255, 0.4);
      transform: translateY(-2px);
    }
    
    /* ==================== PROGRESS CONTAINER ==================== */
    .progress-container {
      position: relative;
      z-index: 1;
      max-width: 900px;
      margin: 0 auto;
      padding: 0 24px;
    }
    
    /* ==================== HERO SECTION ==================== */
    .progress-hero {
      padding: 40px 32px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 24px;
      flex-wrap: wrap;
      background: linear-gradient(160deg, #1E3733 0%, #2F4F4A 55%, #142925 100%);
      border-radius: 20px;
      margin: 24px 0 32px;
    }
    
    .hero-text {
      flex: 1;
      min-width: 300px;
    }
    
    .hero-title {
      font-size: clamp(32px, 5vw, 42px);
      font-weight: 500;
      color: #FBF7EF;
      margin-bottom: 12px;
      line-height: 1.1;
      letter-spacing: -0.01em;
      text-shadow: none;
    }
    
    .hero-subtitle {
      font-size: 16px;
      color: rgba(251, 247, 239, 0.75);
      line-height: 1.6;
    }
    
    .stats-card {
      display: flex;
      align-items: center;
      gap: 20px;
      padding: 20px 28px;
      background: rgba(255, 255, 255, 0.15);
      border: 2px solid rgba(255, 255, 255, 0.2);
      border-radius: 16px;
      backdrop-filter: blur(20px);
    }
    
    .stat-item {
      text-align: center;
    }
    
    .stat-label {
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      color: rgba(255, 255, 255, 0.8);
      margin-bottom: 6px;
    }
    
    .stat-value {
      font-size: 32px;
      font-weight: 900;
      color: #ffffff;
      line-height: 1;
    }
    
    .stat-divider {
      width: 1px;
      height: 40px;
      background: rgba(255, 255, 255, 0.3);
    }
    
    /* ==================== MAIN CARD ==================== */
    .main-card {
      background: rgba(255, 255, 255, 0.95);
      border-radius: 24px;
      padding: 32px;
      backdrop-filter: blur(20px);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
    }
    
    .card-title {
      font-size: 20px;
      font-weight: 700;
      color: #26302C;
      margin-bottom: 24px;
    }
    
    /* ==================== CUSTOMER SELECT SECTION ==================== */
    .customer-select-section {
      position: relative;
      margin-bottom: 24px;
    }
    
    .search-wrapper {
      position: relative;
      display: flex;
      align-items: center;
    }
    
    .search-icon {
      position: absolute;
      left: 14px;
      color: #5B6663;
      pointer-events: none;
    }
    
    .search-wrapper input {
      width: 100%;
      padding: 14px 14px 14px 46px;
      background: white;
      border: 2px solid #E4DCC9;
      border-radius: 12px;
      color: #26302C;
      font-family: 'Inter', sans-serif;
      font-size: 15px;
      outline: none;
      transition: all 0.3s ease;
    }
    
    .search-wrapper input:focus {
      border-color: #C89B4C;
      box-shadow: 0 0 0 4px rgba(200, 155, 76, 0.1);
    }
    
    .search-wrapper input::placeholder {
      color: #93A19C;
    }
    
    .clear-btn {
      position: absolute;
      right: 12px;
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #FBF7EF;
      border: none;
      border-radius: 50%;
      color: #5B6663;
      font-size: 16px;
      cursor: pointer;
      transition: all 0.2s ease;
    }
    
    .clear-btn:hover {
      background: #EFE8D6;
      transform: rotate(90deg);
    }
    
    /* ==================== DROPDOWN ==================== */
    .customer-dropdown {
      position: absolute;
      top: calc(100% + 8px);
      left: 0;
      right: 0;
      max-height: 400px;
      overflow-y: auto;
      background: white;
      border: 2px solid #C89B4C;
      border-radius: 12px;
      box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
      z-index: 10;
      animation: dropdown-appear 0.2s ease;
    }
    
    @keyframes dropdown-appear {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    .customer-option {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 16px;
      cursor: pointer;
      transition: all 0.2s ease;
      border-bottom: 1px solid #EFE8D6;
    }
    
    .customer-option:last-child {
      border-bottom: none;
    }
    
    .customer-option:hover {
      background: rgba(200, 155, 76, 0.1);
    }
    
    .customer-avatar {
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #E4C989, #C89B4C);
      border-radius: 50%;
      font-size: 14px;
      font-weight: 700;
      color: #1E3733;
      flex-shrink: 0;
    }
    
    .customer-info {
      flex: 1;
      min-width: 0;
    }
    
    .customer-name {
      font-size: 15px;
      font-weight: 600;
      color: #26302C;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    
    .customer-email {
      font-size: 13px;
      color: #5B6663;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    
    .progress-badge {
      padding: 4px 10px;
      border-radius: 6px;
      font-size: 11px;
      font-weight: 700;
      flex-shrink: 0;
    }
    
    .badge-not-started {
      background: rgba(91, 102, 99, 0.2);
      color: #5B6663;
    }
    
    .badge-designing {
      background: rgba(200, 155, 76, 0.2);
      color: #C89B4C;
    }
    
    .badge-developing {
      background: rgba(200, 155, 76, 0.2);
      color: #C89B4C;
    }
    
    .badge-testing {
      background: rgba(228, 201, 137, 0.25);
      color: #9C7A34;
    }
    
    .badge-completed {
      background: rgba(63, 148, 99, 0.2);
      color: #3F9463;
    }
    
    /* ==================== NO RESULTS ==================== */
    .no-results {
      padding: 40px 20px;
      text-align: center;
      color: #5B6663;
    }
    
    .no-results svg {
      color: #E4DCC9;
      margin-bottom: 12px;
    }
    
    .no-results p {
      font-size: 15px;
    }
    
    /* ==================== INFO ALERT ==================== */
    .info-alert {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 16px;
      background: rgba(47, 79, 74, 0.15);
      border: 2px solid rgba(47, 79, 74, 0.3);
      border-radius: 12px;
      color: #2F4F4A;
      font-size: 14px;
      margin-bottom: 24px;
    }
    
    /* ==================== CUSTOMER SUMMARY ==================== */
    .customer-summary {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      padding: 20px;
      background: #FBF7EF;
      border: 2px solid #C89B4C;
      border-radius: 12px;
      margin-bottom: 32px;
      flex-wrap: wrap;
      animation: summary-appear 0.3s ease;
    }
    
    @keyframes summary-appear {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    .summary-left {
      display: flex;
      align-items: center;
      gap: 12px;
    }
    
    .summary-avatar {
      width: 48px;
      height: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #E4C989, #C89B4C);
      border-radius: 50%;
      font-size: 16px;
      font-weight: 700;
      color: #1E3733;
    }
    
    .summary-info {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    
    .summary-label {
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: #5B6663;
    }
    
    .summary-name {
      font-size: 16px;
      font-weight: 700;
      color: #26302C;
    }
    
    .summary-badges {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }
    
    .current-badge,
    .next-badge {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 6px 12px;
      border-radius: 8px;
      font-size: 12px;
      font-weight: 700;
    }
    
    .current-badge {
      background: rgba(200, 155, 76, 0.2);
      border: 1px solid rgba(200, 155, 76, 0.4);
      color: #C89B4C;
    }
    
    .next-badge {
      background: rgba(156, 122, 52, 0.2);
      border: 1px solid rgba(156, 122, 52, 0.4);
      color: #9C7A34;
    }
    
    .summary-date {
      font-size: 12px;
      color: #5B6663;
    }
    
    /* ==================== TIMELINE SECTION ==================== */
    .timeline-section {
      margin-bottom: 32px;
      animation: fade-in 0.4s ease;
    }
    
    @keyframes fade-in {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    
    .timeline-title {
      font-size: 18px;
      font-weight: 700;
      color: #26302C;
      margin-bottom: 20px;
    }
    
    .timeline {
      position: relative;
    }
    
    .timeline-item {
      position: relative;
      display: flex;
      gap: 16px;
      margin-bottom: 20px;
    }
    
    .timeline-item:last-child {
      margin-bottom: 0;
    }
    
    .timeline-marker {
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      flex-shrink: 0;
      z-index: 1;
      transition: all 0.3s ease;
    }
    
    .timeline-item.completed .timeline-marker {
      background: linear-gradient(135deg, #3F9463, #6FA37D);
      color: #ffffff;
      box-shadow: 0 4px 15px rgba(63, 148, 99, 0.4);
    }
    
    .timeline-item:not(.completed) .timeline-marker {
      background: #FBF7EF;
      border: 2px solid #E4DCC9;
      color: #93A19C;
    }
    
    .timeline-line {
      position: absolute;
      left: 19px;
      top: 40px;
      width: 2px;
      height: calc(100% - 20px);
      background: #EFE8D6;
    }
    
    .timeline-item.completed .timeline-line {
      background: linear-gradient(180deg, #3F9463, rgba(63, 148, 99, 0.2));
    }
    
    .timeline-content {
      flex: 1;
      padding: 8px 0;
    }
    
    .timeline-step-name {
      font-size: 16px;
      font-weight: 700;
      color: #26302C;
      margin-bottom: 4px;
    }
    
    .timeline-date {
      font-size: 13px;
      color: #5B6663;
    }
    
    /* ==================== STAGE SECTION ==================== */
    .stage-section {
      margin-bottom: 24px;
      animation: fade-in 0.4s ease;
    }
    
    .quick-select {
      margin-bottom: 20px;
    }
    
    .quick-label {
      display: block;
      font-size: 13px;
      font-weight: 700;
      color: #26302C;
      margin-bottom: 12px;
    }
    
    .stage-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }
    
    .stage-chip {
      padding: 8px 16px;
      background: #FBF7EF;
      border: 2px solid #E4DCC9;
      border-radius: 8px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;
      font-family: 'Inter', sans-serif;
    }
    
    .stage-chip:hover {
      background: #EFE8D6;
      transform: translateY(-2px);
    }
    
    .stage-chip.active {
      border-color: #C89B4C;
      background: rgba(200, 155, 76, 0.15);
      color: #C89B4C;
      box-shadow: 0 4px 15px rgba(200, 155, 76, 0.2);
    }
    
    .chip-not-started { color: #5B6663; }
    .chip-designing { color: #C89B4C; }
    .chip-developing { color: #C89B4C; }
    .chip-testing { color: #9C7A34; }
    .chip-completed { color: #3F9463; }
    
    .stage-select-wrapper {
      margin-bottom: 24px;
    }
    
    .stage-select-wrapper label {
      display: block;
      font-size: 13px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: #26302C;
      margin-bottom: 8px;
    }
    
    .stage-select {
      width: 100%;
      padding: 12px 16px;
      background: white;
      border: 2px solid #E4DCC9;
      border-radius: 10px;
      color: #26302C;
      font-family: 'Inter', sans-serif;
      font-size: 15px;
      cursor: pointer;
      outline: none;
      transition: all 0.3s ease;
    }
    
    .stage-select:focus {
      border-color: #C89B4C;
      box-shadow: 0 0 0 4px rgba(200, 155, 76, 0.1);
    }
    
    .stage-select option {
      background: white;
      color: #26302C;
    }
    
    .note-wrapper { margin-top: 16px; }
    .note-wrapper label { display: block; font-size: 13px; font-weight: 600; color: #26302C; margin-bottom: 8px; }
    .note-input {
      width: 100%; padding: 12px 16px; background: white; border: 2px solid #E4DCC9; border-radius: 10px;
      color: #26302C; font-family: 'Inter', sans-serif; font-size: 15px; outline: none; resize: vertical;
    }
    .note-input:focus { border-color: #C89B4C; box-shadow: 0 0 0 4px rgba(200, 155, 76, 0.1); }
    .note-hint { font-size: 12.5px; color: #5B6663; margin-top: 6px; }

    .email-note {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-top: 16px;
      font-size: 12.5px;
      color: #5B6663;
    }
    
    .email-note svg {
      color: #9C7A34;
      flex-shrink: 0;
    }
    
    /* ==================== UPDATE BUTTON ==================== */
    .btn-update {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      width: 100%;
      padding: 16px 32px;
      background: linear-gradient(135deg, #E4C989, #C89B4C);
      border: none;
      border-radius: 12px;
      color: #1E3733;
      font-size: 16px;
      font-weight: 700;
      cursor: pointer;
      transition: all 0.3s ease;
      box-shadow: 0 4px 20px rgba(200, 155, 76, 0.4);
      font-family: 'Inter', sans-serif;
    }
    
    .btn-update:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 6px 30px rgba(200, 155, 76, 0.6);
    }
    
    .btn-update:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
    
    /* ==================== MESSAGE ALERT ==================== */
    .message-alert {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 16px;
      border-radius: 12px;
      font-size: 14px;
      font-weight: 500;
      margin-top: 24px;
      animation: fade-in 0.3s ease;
    }
    
    .message-alert.success {
      background: rgba(63, 148, 99, 0.15);
      border: 2px solid rgba(63, 148, 99, 0.3);
      color: #3F9463;
    }
    
    .message-alert.error {
      background: rgba(179, 85, 74, 0.15);
      border: 2px solid rgba(179, 85, 74, 0.3);
      color: #B3554A;
    }
    
    .message-alert button {
      margin-left: auto;
      background: transparent;
      border: none;
      color: currentColor;
      font-size: 18px;
      cursor: pointer;
      padding: 4px;
      opacity: 0.7;
      transition: opacity 0.2s ease;
    }
    
    .message-alert button:hover {
      opacity: 1;
    }
    
    /* ==================== SNACKBAR ==================== */
    .snackbar {
      position: fixed;
      bottom: 32px;
      right: 32px;
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 16px 24px;
      background: white;
      border: 2px solid #C89B4C;
      border-radius: 12px;
      color: #26302C;
      font-size: 14px;
      box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
      z-index: 2000;
      animation: snackbar-in 0.3s ease;
    }
    
    @keyframes snackbar-in {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    .snackbar button {
      background: transparent;
      border: none;
      color: #26302C;
      font-size: 18px;
      cursor: pointer;
      padding: 4px;
      opacity: 0.7;
      transition: opacity 0.2s ease;
    }
    
    .snackbar button:hover {
      opacity: 1;
    }
    
    /* ==================== TRANSITIONS ==================== */
    .slide-down-enter-active {
      animation: slide-down 0.3s ease;
    }
    
    .slide-down-leave-active {
      animation: slide-down 0.2s ease reverse;
    }
    
    @keyframes slide-down {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    .fade-enter-active, .fade-leave-active {
      transition: opacity 0.3s ease;
    }
    
    .fade-enter-from, .fade-leave-to {
      opacity: 0;
    }
    
    /* ==================== RESPONSIVE ==================== */
    @media (max-width: 768px) {
      .progress-hero {
        flex-direction: column;
        align-items: flex-start;
      }
    
      .stats-card {
        width: 100%;
      }
    
      .nav-container {
        flex-direction: column;
        gap: 12px;
      }
    
      .nav-brand {
        font-size: 12px;
      }
    
      .customer-summary {
        flex-direction: column;
        align-items: flex-start;
      }
    
      .summary-date {
        width: 100%;
      }
    
      .stage-chips {
        flex-direction: column;
      }
    
      .stage-chip {
        width: 100%;
      }
    }
    </style>